import React, { useContext, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import Select from "react-select";

import { H5, P } from "../../atoms/Texts";
import { DarkFormField, DarkSelectInput, DarkTextArea } from "../../atoms/Inputs";
import {
  getCities,
  getNewToken,
  getStates,
} from "../../../shared/helpers/util.country";
import languages from "../../../shared/fixtures/languages";
import telephone_codes from "../../../shared/fixtures/telephone_codes";
import phone_types from "../../../shared/fixtures/phone_types";
import { NewProgramFooter } from "../../organisms/Footers/footer.new.program";
import { selectStyle } from "../../../shared/fixtures/select_style";
import times from "../../../shared/fixtures/times";
import { gmtOptions } from "../../../shared/fixtures/gmt";

const AddProgramFormThree = ({ changeStep, discard, updateFields, saveToDraft }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const {
    register,
    handleSubmit,
    control,
    getValues,
    setValue,
    formState: { errors: formStateErrors },
  } = useForm();

  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [countries, setCountries] = useState([]);

  const changeState = async (state) => {
    const cities = await getCities({ state: state });
    setCities(cities);
  };

  const changeCountry = async (country) => {
    const states = await getStates({ country: country });
    setStates(states);
  };

  const getCountryToken = async () => {
    const countries = await getNewToken();
    setCountries(countries);
  };

  useEffect(() => {
    getCountryToken();
  }, []);

  const updateData = (data) => {
    const schedule = {};

    schedule.timezone = data.timezone;
    schedule.mon = {
      start: data.monday_start,
      end: data.monday_end,
    };
    schedule.tue = {
      start: data.tuesday_start,
      end: data.tuesday_end,
    };
    schedule.wed = {
      start: data.wednesday_start,
      end: data.wednesday_end,
    };
    schedule.thur = {
      start: data.thursday_start,
      end: data.thursday_end,
    };
    schedule.fri = {
      start: data.friday_start,
      end: data.friday_end,
    };
    schedule.sat = {
      start: data.saturday_start,
      end: data.saturday_end,
    };
    schedule.sun = {
      start: data.sunday_start,
      end: data.sunday_end,
    };

    updateFields({ ...data, schedule });
  };

  return (
    <main className="">
      <form onSubmit={handleSubmit(updateData)}>
        <section className="pb-20 w-10/12 organization-container">
          {/* Location */}
          <H5 className="mb-4">Location</H5>
          <div className="flex-grow grid grid-cols-1 md:grid-cols-2 gap-x-5">
            <div key={`location-name`} className="col-span-2">
              <div className="flex items-center justify-between">
                <DarkFormField
                  type="text"
                  id={`location-name`}
                  name={`location-name`}
                  placeholder="Enter name"
                  label="Name"
                  containerClass="flex-grow"
                  {...register("location-name")}
                />
              </div>

              <ErrorMessage
                errors={formStateErrors}
                name={`location-name`}
                as={
                  <P fontSize="12px" className="-mt-4 mb-4 text-red-400 text-sm" />
                }
              >
                {({ messages }) =>
                  messages &&
                  Object.entries(messages).map(([type, message]) => (
                    <P key={type}>{message}</P>
                  ))
                }
              </ErrorMessage>
            </div>

            <div key={`location-address`} className=" col-span-2">
              <div className="flex items-center justify-between">
                <DarkTextArea
                  rows={3}
                  id={`location-address`}
                  name={`location-address`}
                  placeholder="E.g Headquarters, Chicago Branch, West Regional Office"
                  label="Address"
                  containerClass="flex-grow"
                  required
                  {...register("location-address", {})}
                />
              </div>

              <ErrorMessage
                errors={formStateErrors}
                name={`location-address`}
                as={
                  <P fontSize="12px" className="-mt-4 mb-4 text-red-400 text-sm" />
                }
              >
                {({ messages }) =>
                  messages &&
                  Object.entries(messages).map(([type, message]) => (
                    <P key={type}>{message}</P>
                  ))
                }
              </ErrorMessage>
            </div>

            <div className="mb-4">
              <label htmlFor={`location-country`}>
                <P
                  fontSize="14px"
                  fontFamily="semi"
                  color="#47495C"
                  className="mb-2"
                >
                  Country
                  <span
                    className=""
                    style={{
                      color: "#E62250",
                    }}
                  >
                    *
                  </span>
                </P>
              </label>

              <Controller
                control={control}
                id={`location-country`}
                name={`location-country`}
                render={({ field }) => (
                  <Select
                    {...field}
                    styles={selectStyle}
                    autoComplete="true"
                    isLoading={countries?.length < 1}
                    isDisabled={countries?.length < 1}
                    options={countries}
                    onChange={(option) => {
                      setValue("location-stateOrProvince", "");
                      setValue("location-city", "");
                      changeCountry(option.value);
                      field.onChange(option);
                    }}
                    placeholder="--Select a country--"
                    className="w-full"
                  />
                )}
                defaultValue=""
              />
              {formStateErrors[`location-country`] && (
                <P fontSize="12px" className="mt-2 mb-4 text-red-400 text-sm">
                  Select a country
                </P>
              )}
            </div>

            <div key={`location-region`}>
              <div className="flex items-center justify-between">
                <DarkFormField
                  type="text"
                  id={`location-region`}
                  name={`location-region`}
                  placeholder="..."
                  label="Region"
                  containerClass="flex-grow"
                  {...register("location-region")}
                />
              </div>

              <ErrorMessage
                errors={formStateErrors}
                name={`location-region`}
                as={
                  <P fontSize="12px" className="-mt-4 mb-4 text-red-400 text-sm" />
                }
              >
                {({ messages }) =>
                  messages &&
                  Object.entries(messages).map(([type, message]) => (
                    <P key={type}>{message}</P>
                  ))
                }
              </ErrorMessage>
            </div>

            <div className="mb-4">
              <label htmlFor={`location-stateOrProvince`}>
                <P
                  fontSize="14px"
                  fontFamily="semi"
                  color="#47495C"
                  className="mb-2"
                >
                  State or Province
                  <span
                    className=""
                    style={{
                      color: "#E62250",
                    }}
                  >
                    *
                  </span>
                </P>
              </label>
              <Controller
                control={control}
                id={`location-stateOrProvince`}
                name={`location-stateOrProvince`}
                render={({ field }) => (
                  <Select
                    styles={selectStyle}
                    {...field}
                    autoComplete="true"
                    isLoading={states?.length < 1}
                    isDisabled={states?.length < 1}
                    options={states}
                    onChange={(option) => {
                      setValue("location-city", "");
                      changeState(option.value);
                      field.onChange(option);
                    }}
                    placeholder="--Select a state--"
                    className="w-full"
                  />
                )}
                defaultValue=""
              />
              {formStateErrors[`location-stateOrProvince`] && (
                <P fontSize="12px" className="mt-2 mb-4 text-red-400 text-sm">
                  Select a state
                </P>
              )}
            </div>

            <div className="mb-4">
              <label htmlFor={`location-city`}>
                <P
                  fontSize="14px"
                  fontFamily="semi"
                  color="#47495C"
                  className="mb-2"
                >
                  City
                </P>
              </label>
              <Controller
                control={control}
                id={`location-city`}
                name={`location-city`}
                render={({ field }) => (
                  <Select
                    {...field}
                    styles={selectStyle}
                    autoComplete="true"
                    isLoading={cities?.length < 1}
                    isDisabled={cities?.length < 1}
                    options={cities}
                    placeholder="--Select a city--"
                    className="w-full"
                  />
                )}
                defaultValue=""
              />
            </div>

            <div key={`location-zipCode`}>
              <div className="flex items-center justify-between">
                <DarkFormField
                  type="number"
                  id={`location-zipCode`}
                  name={`location-zipCode`}
                  placeholder="Enter zip code"
                  label="Zip Code"
                  containerClass="flex-grow"
                  {...register("location-zipCode", {
                    pattern: {
                      value: /[0-9]+/,
                      message: "Please enter a valid Zip Code.",
                    },
                  })}
                />
              </div>

              <ErrorMessage
                errors={formStateErrors}
                name={`location-zipCode`}
                as={
                  <P fontSize="12px" className="-mt-4 mb-4 text-red-400 text-sm" />
                }
              >
                {({ messages }) =>
                  messages &&
                  Object.entries(messages).map(([type, message]) => (
                    <P key={type}>{message}</P>
                  ))
                }
              </ErrorMessage>
            </div>

            <div className="mb-4">
              <label htmlFor={`location-languages`}>
                <P
                  fontSize="14px"
                  fontFamily="semi"
                  color="#47495C"
                  className="mb-2"
                >
                  Languages
                  <span
                    className=""
                    style={{
                      color: "#E62250",
                    }}
                  >
                    *
                  </span>
                </P>
              </label>
              <Controller
                control={control}
                name={`location-languages`}
                render={({ field }) => (
                  <Select
                    {...field}
                    styles={selectStyle}
                    isMulti
                    options={languages}
                  />
                )}
                defaultValue={[]}
              />

              {formStateErrors[`location-languages`] && (
                <P fontSize="12px" className="mt-2 mb-4 text-red-400 text-sm">
                  Select at least one language
                </P>
              )}
            </div>

            <div key={`location-transportation`} className=" col-span-2">
              <div className="flex items-center justify-between">
                <DarkTextArea
                  rows={3}
                  id={`location-transportation`}
                  name={`location-transportation`}
                  placeholder="Enter transportation"
                  label="Transportation"
                  containerClass="flex-grow"
                  {...register("location-transportation")}
                />
              </div>

              <ErrorMessage
                errors={formStateErrors}
                name={`location-transportation`}
                as={
                  <P fontSize="12px" className="-mt-4 mb-4 text-red-400 text-sm" />
                }
              >
                {({ messages }) =>
                  messages &&
                  Object.entries(messages).map(([type, message]) => (
                    <P key={type}>{message}</P>
                  ))
                }
              </ErrorMessage>
            </div>

            <div
              key={`location-accessibiltyForDisabilities`}
              className=" col-span-2"
            >
              <div className="flex items-center justify-between">
                <DarkTextArea
                  rows={3}
                  id={`location-accessibiltyForDisabilities`}
                  name={`location-accessibiltyForDisabilities`}
                  placeholder="Enter Accessibility for disability "
                  label="Accessibility for Disabilities"
                  containerClass="flex-grow"
                  {...register("location-accessibiltyForDisabilities")}
                />
              </div>

              <ErrorMessage
                errors={formStateErrors}
                name={`location-accessibiltyForDisabilities`}
                as={
                  <P fontSize="12px" className="-mt-4 mb-4 text-red-400 text-sm" />
                }
              >
                {({ messages }) =>
                  messages &&
                  Object.entries(messages).map(([type, message]) => (
                    <P key={type}>{message}</P>
                  ))
                }
              </ErrorMessage>
            </div>
          </div>

          {/* timezone */}
          <H5 className="mb-4 mt-16">Operational Hours</H5>
          <section>
            <div className="w-3/5">
              <DarkSelectInput
                type="select"
                id="timezone"
                name="timezone"
                className="py-1 px-2"
                label="Time Zone"
                defaultValue={""}
                iconBeforeClass=""
                {...register("timezone")}
              >
                <option value="" disabled>
                  --
                </option>

                {gmtOptions.map((gmt) => (
                  <option key={gmt.value} value={gmt.value}>
                    {gmt.label}
                  </option>
                ))}
              </DarkSelectInput>
            </div>

            <div className="flex w-3/5 mt-4 justify-between">
              <label htmlFor="monday" style={{ width: "100px" }}>
                <P
                  fontSize="14px"
                  fontFamily="semi"
                  color="#47495C"
                  className="mt-4"
                >
                  Monday
                </P>
              </label>
              <div className="flex-grow ml-4">
                <div className="flex items-center">
                  <div className="flex-grow mr-4">
                    <DarkSelectInput
                      type="select"
                      id="monday_start"
                      name="monday_start"
                      className="py-1 px-2 flex-grow"
                      defaultValue={""}
                      iconBeforeClass=""
                      marginBottom="0"
                      {...register("monday_start")}
                    >
                      <option value="" disabled>
                        --
                      </option>
                      {times.map((time) => (
                        <option key={time.value} value={time.value}>
                          {time.label}
                        </option>
                      ))}
                    </DarkSelectInput>
                  </div>
                  -
                  <div className="flex-grow ml-4">
                    <DarkSelectInput
                      type="select"
                      id="monday_end"
                      name="monday_end"
                      className="py-1 px-2 flex-grow"
                      defaultValue={""}
                      iconBeforeClass=""
                      marginBottom="0"
                      {...register("monday_end")}
                    >
                      <option value="" disabled>
                        --
                      </option>
                      {times.map((time) => (
                        <option key={time.value} value={time.value}>
                          {time.label}
                        </option>
                      ))}
                    </DarkSelectInput>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex w-3/5 mt-4 justify-between">
              <label htmlFor="tuesday" style={{ width: "100px" }}>
                <P
                  fontSize="14px"
                  fontFamily="semi"
                  color="#47495C"
                  className="mt-4"
                >
                  Tuesday
                </P>
              </label>
              <div className="flex-grow ml-4">
                <div className="flex items-center">
                  <div className="flex-grow mr-4">
                    <DarkSelectInput
                      type="select"
                      id="tuesday_start"
                      name="tuesday_start"
                      className="py-1 px-2 flex-grow"
                      defaultValue={""}
                      iconBeforeClass=""
                      marginBottom="0"
                      {...register("tuesday_start")}
                    >
                      <option value="" disabled>
                        --
                      </option>
                      {times.map((time) => (
                        <option key={time.value} value={time.value}>
                          {time.label}
                        </option>
                      ))}
                    </DarkSelectInput>
                  </div>
                  -
                  <div className="flex-grow ml-4">
                    <DarkSelectInput
                      type="select"
                      id="tuesday_end"
                      name="tuesday_end"
                      className="py-1 px-2 flex-grow"
                      defaultValue={""}
                      iconBeforeClass=""
                      marginBottom="0"
                      {...register("tuesday_end")}
                    >
                      <option value="" disabled>
                        --
                      </option>
                      {times.map((time) => (
                        <option key={time.value} value={time.value}>
                          {time.label}
                        </option>
                      ))}
                    </DarkSelectInput>
                  </div>
                </div>
                {/* <div className="flex items-center justify-between mt-1">
                  <div className="flex-grow">
                    <ErrorMessage
                      errors={errors}
                      name="tuesday_start"
                      as={
                        <P fontSize="12px" className="mb-4 text-red-400 text-sm" />
                      }
                    >
                      {({ messages }) =>
                        messages &&
                        Object.entries(messages).map(([type, message]) => (
                          <P key={type}>{message}</P>
                        ))
                      }
                    </ErrorMessage>
                  </div>
                  <ErrorMessage
                    errors={errors}
                    name="wednesday_end"
                    as={<P fontSize="12px" className="mb-4 text-red-400 text-sm" />}
                  >
                    {({ messages }) =>
                      messages &&
                      Object.entries(messages).map(([type, message]) => (
                        <P key={type}>{message}</P>
                      ))
                    }
                  </ErrorMessage>
                </div> */}
              </div>
            </div>

            <div className="flex w-3/5 mt-4 justify-between">
              <label htmlFor="wednesday" style={{ width: "100px" }}>
                <P
                  fontSize="14px"
                  fontFamily="semi"
                  color="#47495C"
                  className="mt-4"
                >
                  Wednesday
                </P>
              </label>
              <div className="flex-grow ml-4">
                <div className="flex items-center">
                  <div className="flex-grow mr-4">
                    <DarkSelectInput
                      type="select"
                      id="wednesday_start"
                      name="wednesday_start"
                      className="py-1 px-2 flex-grow"
                      defaultValue={""}
                      iconBeforeClass=""
                      marginBottom="0"
                      {...register("wednesday_start")}
                    >
                      <option value="" disabled>
                        --
                      </option>
                      {times.map((time) => (
                        <option key={time.value} value={time.value}>
                          {time.label}
                        </option>
                      ))}
                    </DarkSelectInput>
                  </div>
                  -
                  <div className="flex-grow ml-4">
                    <DarkSelectInput
                      type="select"
                      id="wednesday_end"
                      name="wednesday_end"
                      className="py-1 px-2 flex-grow"
                      defaultValue={""}
                      iconBeforeClass=""
                      marginBottom="0"
                      {...register("wednesday_end")}
                    >
                      <option value="" disabled>
                        --
                      </option>
                      {times.map((time) => (
                        <option key={time.value} value={time.value}>
                          {time.label}
                        </option>
                      ))}
                    </DarkSelectInput>
                  </div>
                </div>
                {/* <div className="flex items-center justify-between mt-1">
                  <div className="flex-grow">
                    <ErrorMessage
                      errors={errors}
                      name="wednesday_start"
                      as={
                        <P fontSize="12px" className="mb-4 text-red-400 text-sm" />
                      }
                    >
                      {({ messages }) =>
                        messages &&
                        Object.entries(messages).map(([type, message]) => (
                          <P key={type}>{message}</P>
                        ))
                      }
                    </ErrorMessage>
                  </div>
                  <ErrorMessage
                    errors={errors}
                    name="wednesday_end"
                    as={<P fontSize="12px" className="mb-4 text-red-400 text-sm" />}
                  >
                    {({ messages }) =>
                      messages &&
                      Object.entries(messages).map(([type, message]) => (
                        <P key={type}>{message}</P>
                      ))
                    }
                  </ErrorMessage>
                </div> */}
              </div>
            </div>

            <div className="flex w-3/5 mt-4 justify-between">
              <label htmlFor="thursday" style={{ width: "100px" }}>
                <P
                  fontSize="14px"
                  fontFamily="semi"
                  color="#47495C"
                  className="mt-4"
                >
                  Thursday
                </P>
              </label>
              <div className="flex-grow ml-4">
                <div className="flex items-center">
                  <div className="flex-grow mr-4">
                    <DarkSelectInput
                      type="select"
                      id="thursday_start"
                      name="thursday_start"
                      className="py-1 px-2 flex-grow"
                      defaultValue={""}
                      iconBeforeClass=""
                      marginBottom="0"
                      {...register("thursday_start")}
                    >
                      <option value="" disabled>
                        --
                      </option>
                      {times.map((time) => (
                        <option key={time.value} value={time.value}>
                          {time.label}
                        </option>
                      ))}
                    </DarkSelectInput>
                  </div>
                  -
                  <div className="flex-grow ml-4">
                    <DarkSelectInput
                      type="select"
                      id="thursday_end"
                      name="thursday_end"
                      className="py-1 px-2 flex-grow"
                      defaultValue={""}
                      iconBeforeClass=""
                      marginBottom="0"
                      {...register("thursday_end")}
                    >
                      <option value="" disabled>
                        --
                      </option>
                      {times.map((time) => (
                        <option key={time.value} value={time.value}>
                          {time.label}
                        </option>
                      ))}
                    </DarkSelectInput>
                  </div>
                </div>
                {/* <div className="flex items-center justify-between mt-1">
                  <div className="flex-grow">
                    <ErrorMessage
                      errors={errors}
                      name="thursday_start"
                      as={
                        <P fontSize="12px" className="mb-4 text-red-400 text-sm" />
                      }
                    >
                      {({ messages }) =>
                        messages &&
                        Object.entries(messages).map(([type, message]) => (
                          <P key={type}>{message}</P>
                        ))
                      }
                    </ErrorMessage>
                  </div>
                  <ErrorMessage
                    errors={errors}
                    name="thursday_end"
                    as={<P fontSize="12px" className="mb-4 text-red-400 text-sm" />}
                  >
                    {({ messages }) =>
                      messages &&
                      Object.entries(messages).map(([type, message]) => (
                        <P key={type}>{message}</P>
                      ))
                    }
                  </ErrorMessage>
                </div> */}
              </div>
            </div>

            <div className="flex w-3/5 mt-4 justify-between">
              <label htmlFor="friday" style={{ width: "100px" }}>
                <P
                  fontSize="14px"
                  fontFamily="semi"
                  color="#47495C"
                  className="mt-4"
                >
                  Friday
                </P>
              </label>
              <div className="flex-grow ml-4">
                <div className="flex items-center">
                  <div className="flex-grow mr-4">
                    <DarkSelectInput
                      type="select"
                      id="friday_start"
                      name="friday_start"
                      className="py-1 px-2 flex-grow"
                      defaultValue={""}
                      iconBeforeClass=""
                      marginBottom="0"
                      {...register("friday_start")}
                    >
                      <option value="" disabled>
                        --
                      </option>
                      {times.map((time) => (
                        <option key={time.value} value={time.value}>
                          {time.label}
                        </option>
                      ))}
                    </DarkSelectInput>
                  </div>
                  -
                  <div className="flex-grow ml-4">
                    <DarkSelectInput
                      type="select"
                      id="friday_end"
                      name="friday_end"
                      className="py-1 px-2 flex-grow"
                      defaultValue={""}
                      iconBeforeClass=""
                      marginBottom="0"
                      {...register("friday_end")}
                    >
                      <option value="" disabled>
                        --
                      </option>
                      {times.map((time) => (
                        <option key={time.value} value={time.value}>
                          {time.label}
                        </option>
                      ))}
                    </DarkSelectInput>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex w-3/5 mt-4 justify-between">
              <label htmlFor="saturday" style={{ width: "100px" }}>
                <P
                  fontSize="14px"
                  fontFamily="semi"
                  color="#47495C"
                  className="mt-4"
                >
                  Saturday
                </P>
              </label>
              <div className="flex-grow ml-4">
                <div className="flex items-center">
                  <div className="flex-grow mr-4">
                    <DarkSelectInput
                      type="select"
                      id="saturday_start"
                      name="saturday_start"
                      className="py-1 px-2 flex-grow"
                      defaultValue={""}
                      iconBeforeClass=""
                      marginBottom="0"
                      {...register("saturday_start")}
                    >
                      <option value="" disabled>
                        --
                      </option>
                      {times.map((time) => (
                        <option key={time.value} value={time.value}>
                          {time.label}
                        </option>
                      ))}
                    </DarkSelectInput>
                  </div>
                  -
                  <div className="flex-grow ml-4">
                    <DarkSelectInput
                      type="select"
                      id="saturday_end"
                      name="saturday_end"
                      className="py-1 px-2 flex-grow"
                      defaultValue={""}
                      iconBeforeClass=""
                      marginBottom="0"
                      {...register("saturday_end")}
                    >
                      <option value="" disabled>
                        --
                      </option>
                      {times.map((time) => (
                        <option key={time.value} value={time.value}>
                          {time.label}
                        </option>
                      ))}
                    </DarkSelectInput>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex w-3/5 mt-4 justify-between">
              <label htmlFor="sunday" style={{ width: "100px" }}>
                <P
                  fontSize="14px"
                  fontFamily="semi"
                  color="#47495C"
                  className="mt-4"
                >
                  Sunday
                </P>
              </label>
              <div className="flex-grow ml-4">
                <div className="flex items-center">
                  <div className="flex-grow mr-4">
                    <DarkSelectInput
                      type="select"
                      id="sunday_start"
                      name="sunday_start"
                      className="py-1 px-2 flex-grow"
                      defaultValue={""}
                      iconBeforeClass=""
                      marginBottom="0"
                      {...register("sunday_start")}
                    >
                      <option value="" disabled>
                        --
                      </option>
                      {times.map((time) => (
                        <option key={time.value} value={time.value}>
                          {time.label}
                        </option>
                      ))}
                    </DarkSelectInput>
                  </div>
                  -
                  <div className="flex-grow ml-4">
                    <DarkSelectInput
                      type="select"
                      id="sunday_end"
                      name="sunday_end"
                      className="py-1 px-2 flex-grow"
                      defaultValue={""}
                      iconBeforeClass=""
                      marginBottom="0"
                      {...register("sunday_end")}
                    >
                      <option value="" disabled>
                        --
                      </option>
                      {times.map((time) => (
                        <option key={time.value} value={time.value}>
                          {time.label}
                        </option>
                      ))}
                    </DarkSelectInput>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Phone */}
          <H5 className="mb-4 mt-16">Phone</H5>
          <div className="flex-grow grid grid-cols-1 md:grid-cols-2 gap-x-5">
            <div key={`phone-number`}>
              <div className="flex items-center justify-between">
                <DarkFormField
                  type="tel"
                  id={`phone-number`}
                  name={`phone-number`}
                  placeholder="Enter phone number"
                  label="Number"
                  containerClass="flex-grow"
                  {...register("phone-number", {})}
                />
              </div>

              <ErrorMessage
                errors={formStateErrors}
                name={`phone-number`}
                as={
                  <P fontSize="12px" className="-mt-4 mb-4 text-red-400 text-sm" />
                }
              >
                {({ messages }) =>
                  messages &&
                  Object.entries(messages).map(([type, message]) => (
                    <P key={type}>{message}</P>
                  ))
                }
              </ErrorMessage>
            </div>

            <div key={`phone-extension`} className="mb-4">
              <label htmlFor="opCategory">
                <P
                  fontSize="14px"
                  fontFamily="semi"
                  color="#47495C"
                  className="mb-2"
                >
                  Extension
                  <span
                    className=""
                    style={{
                      color: "#E62250",
                    }}
                  >
                    *
                  </span>
                </P>
              </label>
              <Controller
                control={control}
                id={`phone-extension`}
                name={`phone-extension`}
                render={({ field }) => (
                  <Select
                    {...field}
                    styles={selectStyle}
                    autoComplete="true"
                    options={telephone_codes}
                    placeholder="--Select an extension--"
                    className="w-full"
                    defaultValue=""
                  />
                )}
                defaultValue=""
              />
              {formStateErrors[`phone-extension`] && (
                <P fontSize="12px" className="mt-2 mb-4 text-red-400 text-sm">
                  Select an extension
                </P>
              )}
            </div>

            <div key={`phone-type`} className="mb-4">
              <label htmlFor="opCategory">
                <P
                  fontSize="14px"
                  fontFamily="semi"
                  color="#47495C"
                  className="mb-2"
                >
                  Phone Type
                  <span
                    className=""
                    style={{
                      color: "#E62250",
                    }}
                  >
                    *
                  </span>
                </P>
              </label>
              <Controller
                control={control}
                id={`phone-type`}
                name={`phone-type`}
                render={({ field }) => (
                  <Select
                    styles={selectStyle}
                    {...field}
                    autoComplete="true"
                    options={phone_types}
                    placeholder="--Select an type--"
                    className="w-full"
                  />
                )}
                defaultValue=""
              />
              {formStateErrors[`phone-type`] && (
                <P fontSize="12px" className="mt-2 mb-4 text-red-400 text-sm">
                  Select an phone type
                </P>
              )}
            </div>

            <div className="mb-4">
              <label htmlFor={`phone-language`}>
                <P
                  fontSize="14px"
                  fontFamily="semi"
                  color="#47495C"
                  className="mb-2"
                >
                  Language
                  <span
                    className=""
                    style={{
                      color: "#E62250",
                    }}
                  >
                    *
                  </span>
                </P>
              </label>
              <Controller
                control={control}
                name={`phone-language`}
                render={({ field }) => (
                  <Select {...field} styles={selectStyle} options={languages} />
                )}
                defaultValue=""
              />

              {formStateErrors[`phone-language`] && (
                <P fontSize="12px" className="mt-2 mb-4 text-red-400 text-sm">
                  Select a language
                </P>
              )}
            </div>
          </div>
        </section>
        {/* <PrimaryButton onClick={validateForm}>Continue</PrimaryButton> */}
        <NewProgramFooter
          changeStep={updateFields}
          submitForm={updateData}
          lastStep
          discard={discard}
          saveToDraft={() => {
            const values = getValues();
            saveToDraft(values);
          }}
        />
      </form>
    </main>
  );
};

export default AddProgramFormThree;
